import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useEffect, useState } from "react";
import { GD } from "src/GD";
import { getAnomaliesRequest, parseOverviewAnomalies, type AnomaliesScope, type TrafficChange, type OverviewAnomalies } from "./overviewAnomalies";

type AnomaliesState = { key: string; busy: boolean; changes: TrafficChange[] | null; result?: OverviewAnomalies; error?: string };
const REQUEST_TIMEOUT_MS = 45_000;
const REQUEST_DELAY_MS = 250;

export function useOverviewAnomalies({ targetId, targetType, projectId, userId }: AnomaliesScope) {
	useLocale();
	const key = JSON.stringify([userId, projectId, targetType, targetId]);
	const [attempt, setAttempt] = useState(0);
	const [state, setState] = useState<AnomaliesState>({ key, busy: true, changes: null });

	useEffect(() => {
		let cancelled = false;
		const controller = new AbortController();
		let deadline: ReturnType<typeof setTimeout> | undefined;
		const subscription = GD.S_AUTH_LOG_OUT.subscribe(() => {
			cancelled = true;
			controller.abort();
			setState({ key, busy: false, changes: null });
		});
		setState({ key, busy: true, changes: null });
		const checkContext = async () => {
			const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
			if (cancelled || controller.signal.aborted || profile?.id !== userId || project?.id !== projectId) throw new Error("Context changed");
		};
		const timer = setTimeout(() => {
			if (cancelled) return;
			deadline = setTimeout(() => {
				controller.abort();
				if (!cancelled) setState({ key, busy: false, changes: null, error: msg("message_7fbb4eae") });
			}, REQUEST_TIMEOUT_MS);
			void (async () => {
				try {
					await checkContext();
					const response = await GD.REQ_API.request({
						...getAnomaliesRequest({ targetId, targetType, projectId, userId }),
						signal: controller.signal,
					});
					await checkContext();
					if (response?.error) throw new Error(msg("anomaliesRequestFailed"));
					const result = parseOverviewAnomalies(response?.data, { targetId, targetType, projectId, userId });
					setState({ key, busy: false, changes: result?.changes ?? null, result: result ?? undefined });
				} catch {
					if (!cancelled && !controller.signal.aborted) setState({ key, busy: false, changes: null, error: msg("message_fd04b8e2") });
				} finally { clearTimeout(deadline); }
			})();
		}, REQUEST_DELAY_MS);
		return () => {
			cancelled = true;
			controller.abort();
			clearTimeout(timer);
			clearTimeout(deadline);
			GD.S_AUTH_LOG_OUT.unsubscribe(subscription);
		};
	}, [key, targetId, targetType, projectId, userId, attempt]);

	return {
		...(state.key === key ? state : { key, busy: true, changes: null }),
		retry: () => setAttempt(value => value + 1),
	};
}
